import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSkeleton } from '../DashboardLoader';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Tag,
  TrendingUp,
  Search,
  Calendar,
  DollarSign,
  BarChart3,
  Image as ImageIcon,
  Upload,
  X,
  Video,
  MousePointer,
  Target,
  TrendingDown,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import promotionApi from '../../../services/promotionApi';
import API_BASE_URL from '../../../config/api';
import { formatCurrency, getRestaurantDishes, getRestaurantMenus } from '../../../services/restaurantApi';
import PromotionEstimator from '../modals/PromotionEstimator';
import { getPromotionPerformance } from '../../../services/promotionTracking';
import PromotionPaymentModal from '../modals/PromotionPaymentModal';
import namkeFallback from '../../../assets/namke-fallback.png';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const FIELD_LABEL = { display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '6px' };
const inputStyle = { width: '100%', minHeight: '46px', padding: '0 18px', background: '#FBF9D9', border: '1px solid rgba(5,16,4,0.12)', borderRadius: '999px', fontSize: '14.5px', color: '#051004', outline: 'none', fontFamily: 'Poppins, sans-serif' };
const btnPrimary = { border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', minHeight: '46px', padding: '0 24px', borderRadius: '999px', whiteSpace: 'nowrap' };
const btnGhost = { border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', minHeight: '46px', padding: '0 24px', borderRadius: '999px', whiteSpace: 'nowrap' };
const iconBtn = { border: 'none', cursor: 'pointer', background: 'rgba(252,251,245,0.92)', width: '32px', height: '32px', borderRadius: '999px', display: 'grid', placeItems: 'center', color: 'rgba(5,16,4,0.6)' };

const optionCard = (active) => ({
  padding: '12px 14px', borderRadius: '16px', cursor: 'pointer', textAlign: 'left',
  border: active ? `2px solid ${PRIMARY}` : '1px solid rgba(5,16,4,0.14)',
  background: active ? '#F1F8E8' : 'transparent',
});

const STATUS_STYLE = {
  active: { bg: '#DDEECB', fg: '#054625', label: 'Active' },
  paused: { bg: '#F4F1E2', fg: '#393A30', label: 'En pause' },
  draft: { bg: '#F4F1E2', fg: '#393A30', label: 'Brouillon' },
  expired: { bg: '#FEF0EA', fg: '#A32C0F', label: 'Expirée' },
  cancelled: { bg: '#FEF0EA', fg: '#A32C0F', label: 'Annulée' },
};

/** Prix décimaux (€) : accepte la virgule dans les champs texte éventuels */
function parseMoneyInput(v) {
  if (v === '' || v === null || v === undefined) return 0;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

const RestaurantPromotions = () => {
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    search: ''
  });
  const [stats, setStats] = useState(null);
  const [selectedPromotionForPerformance, setSelectedPromotionForPerformance] = useState(null);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [insightsStats, setInsightsStats] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [promotionToPay, setPromotionToPay] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);

  useEffect(() => {
    fetchPromotions();
    fetchStats();
  }, [filters]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const data = await promotionApi.getRestaurantPromotions({
        status: filters.status !== 'all' ? filters.status : undefined,
        promotionType: filters.type !== 'all' ? filters.type : undefined,
        search: filters.search || undefined
      });
      const promotionsList = Array.isArray(data) ? data : (data.promotions || []);
      setPromotions(promotionsList);
      // Recharger les insights après avoir récupéré les promotions
      if (promotionsList.length > 0) {
        fetchInsights(promotionsList);
      } else {
        setInsightsStats(null);
      }
    } catch (err) {
      console.error('Erreur fetch promotions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await promotionApi.getPromotionStats();
      setStats(data);
    } catch (err) {
      console.error('Erreur fetch stats:', err);
    }
  };

  const fetchInsights = async (promotionsList = null) => {
    try {
      setLoadingInsights(true);
      // Utiliser la liste fournie ou l'état actuel
      const promotionsToUse = promotionsList || promotions;
      // Récupérer les stats de performance pour toutes les promotions actives
      const activePromotions = promotionsToUse.filter(p => p.status === 'active');

      if (activePromotions.length === 0) {
        setInsightsStats({
          impressions: 0,
          clicks: 0,
          conversions: 0,
          clickThroughRate: 0,
          conversionRate: 0,
          totalRevenue: 0,
          revenuePerClick: 0
        });
        return;
      }

      let totalImpressions = 0;
      let totalClicks = 0;
      let totalConversions = 0;
      let totalRevenue = 0;

      // Pour chaque promotion active, récupérer ses stats
      for (const promotion of activePromotions) {
        try {
          const perfStats = await getPromotionPerformance(promotion.id, 30);
          totalImpressions += perfStats.impressions || 0;
          totalClicks += perfStats.clicks || 0;
          totalConversions += perfStats.conversions || 0;
          totalRevenue += perfStats.total_revenue || 0;
        } catch (err) {
          // Ignorer les erreurs pour les promotions sans stats
          console.warn(`Pas de stats pour la promotion ${promotion.id}`);
        }
      }

      const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
      const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0;
      const revenuePerClick = totalClicks > 0 ? (totalRevenue / totalClicks) : 0;

      setInsightsStats({
        impressions: totalImpressions,
        clicks: totalClicks,
        conversions: totalConversions,
        clickThroughRate: ctr,
        conversionRate: conversionRate,
        totalRevenue: totalRevenue,
        revenuePerClick: revenuePerClick
      });
    } catch (err) {
      console.error('Erreur fetch insights:', err);
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleCreatePromotion = async (promotionData) => {
    try {
      // Créer la promotion
      const createdPromotion = await promotionApi.createPromotion(promotionData);

      // Si la promotion nécessite un paiement, afficher le modal de paiement
      if (promotionData.requires_payment && promotionData.promotion_fee > 0) {
        setShowCreateModal(false);
        setPromotionToPay(createdPromotion);
        setPaymentAmount(promotionData.promotion_fee);
        setShowPaymentModal(true);
      } else {
      setShowCreateModal(false);
      fetchPromotions();
      fetchStats();
      }
    } catch (err) {
      console.error('Erreur création promotion:', err);
      setError(err.message);
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setPromotionToPay(null);
    setPaymentAmount(0);
    fetchPromotions();
    fetchStats();
  };

  const handleUpdatePromotion = async (promotionId, updateData) => {
    try {
      await promotionApi.updatePromotion(promotionId, updateData);
      setEditingPromotion(null);
      fetchPromotions();
      fetchStats();
    } catch (err) {
      console.error('Erreur mise à jour promotion:', err);
      setError(err.message);
    }
  };

  const handleDeletePromotion = async (promotionId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) {
      try {
        await promotionApi.deletePromotion(promotionId);
        fetchPromotions();
        fetchStats();
      } catch (err) {
        console.error('Erreur suppression promotion:', err);
        setError(err.message);
      }
    }
  };

  const handleToggleStatus = async (promotionId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    try {
      await promotionApi.updatePromotion(promotionId, { status: newStatus });
      fetchPromotions();
    } catch (err) {
      console.error('Erreur changement statut:', err);
      setError(err.message);
    }
  };

  const handleViewPerformance = async (promotionId) => {
    try {
      setLoadingPerformance(true);
      setSelectedPromotionForPerformance(promotionId);
      const stats = await getPromotionPerformance(promotionId, 30);
      setPerformanceStats(stats);
    } catch (err) {
      console.error('Erreur récupération performances:', err);
      setError(err.message);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_STYLE[status] || STATUS_STYLE.draft;
    return (
      <span style={{ background: config.bg, color: config.fg, fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
        {config.label}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      percentage: 'Réduction %',
      fixed_amount: 'Réduction fixe',
      buy_one_get_one: 'Achetez 1, obtenez 1',
      combo: 'Menu combo',
      flash_sale: 'Vente flash',
      seasonal: 'Saisonnière'
    };
    return labels[type] || type;
  };

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* Header */}
      <div className="flex items-end gap-4 flex-wrap">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            {promotions.length} promotion{promotions.length > 1 ? 's' : ''}
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Promotions</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2"
          style={{ ...btnPrimary, marginLeft: 'auto' }}
        >
          <Plus size={16} />
          Nouvelle promotion
        </button>
      </div>

      {/* Statistiques générales */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))', gap: 'clamp(12px, 1.6vw, 18px)' }}>
          <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <span style={FIELD_LABEL}>Total</span>
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{stats.total_promotions}</strong>
          </div>
          <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <span style={FIELD_LABEL}>Actives</span>
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em', color: PRIMARY }}>{stats.active_promotions}</strong>
          </div>
          <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <span style={FIELD_LABEL}>Utilisations</span>
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{stats.total_uses}</strong>
          </div>
          <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <span style={FIELD_LABEL}>Réduction moy.</span>
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{stats.average_discount ? `${Math.round(stats.average_discount)}%` : '—'}</strong>
          </div>
        </div>
      )}

      {/* Insights des publicités */}
      <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(18px, 2.2vw, 26px)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Insights des publicités</h2>
        {loadingInsights ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse" style={{ background: '#FBF9D9', borderRadius: '20px', height: '76px' }} />
            ))}
          </div>
        ) : insightsStats ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' }}>
            {[
              { label: 'Impressions', value: insightsStats.impressions.toLocaleString(), Icon: Eye },
              { label: 'Clics', value: insightsStats.clicks.toLocaleString(), Icon: MousePointer },
              { label: 'Conversions', value: insightsStats.conversions.toLocaleString(), Icon: Target },
              { label: 'Taux de clic (CTR)', value: `${insightsStats.clickThroughRate.toFixed(2)}%`, Icon: TrendingUp },
              { label: 'Taux de conversion', value: `${insightsStats.conversionRate.toFixed(2)}%`, Icon: BarChart3 },
              { label: 'Revenu total', value: formatCurrency(insightsStats.totalRevenue || 0), Icon: DollarSign },
              { label: 'Revenu par clic', value: formatCurrency(insightsStats.revenuePerClick || 0), Icon: TrendingDown },
            ].map(({ label, value, Icon }) => (
              <div key={label} style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span className="flex items-center gap-2" style={FIELD_LABEL}><Icon size={13} />{label}</span>
                <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '20px' }}>{value}</strong>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.55)', textAlign: 'center', padding: '12px' }}>Aucune donnée d'insights disponible</p>
        )}
      </section>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <label className="flex items-center gap-2.5 flex-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 18px', minHeight: '46px' }}>
          <Search size={16} style={{ color: PRIMARY, flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Rechercher une promotion..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14.5px', color: '#051004', outline: 'none' }}
          />
        </label>

        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="sm:w-52"
          style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '46px', fontSize: '14px', color: '#051004', outline: 'none' }}
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Active</option>
          <option value="paused">En pause</option>
          <option value="draft">Brouillon</option>
          <option value="expired">Expirée</option>
        </select>

        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="sm:w-52"
          style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '46px', fontSize: '14px', color: '#051004', outline: 'none' }}
        >
          <option value="all">Tous les types</option>
          <option value="percentage">Réduction %</option>
          <option value="fixed_amount">Réduction fixe</option>
          <option value="buy_one_get_one">Achetez 1, obtenez 1</option>
          <option value="combo">Menu combo</option>
          <option value="flash_sale">Vente flash</option>
          <option value="seasonal">Saisonnière</option>
        </select>
      </div>

      {/* Liste des promotions */}
      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error?.message || error?.toString() || 'Une erreur est survenue'}
        </div>
      )}

      {promotions.length === 0 ? (
        <div style={{ background: '#FCFBF5', borderRadius: '30px', textAlign: 'center', padding: '56px 24px' }}>
          <span style={{ width: '76px', height: '76px', borderRadius: '999px', background: '#F4F1E2', display: 'inline-grid', placeItems: 'center', marginBottom: '16px', color: 'rgba(5,16,4,0.35)' }}>
            <Tag size={30} />
          </span>
          <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '19px' }}>Aucune promotion</h3>
          <p style={{ margin: '0 0 18px', fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>Créez votre première promotion pour commencer</p>
          <button type="button" onClick={() => setShowCreateModal(true)} style={btnPrimary}>
            Créer une promotion
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 'clamp(14px, 1.8vw, 22px)' }}>
          {promotions.map((promotion) => (
            <div
              key={promotion.id}
              style={{ background: '#FCFBF5', borderRadius: '26px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              {/* Image */}
              <div className="relative" style={{ height: '180px', overflow: 'hidden', background: '#F4F1E2' }}>
                <img
                  src={promotion.media_url || promotion.image_url || namkeFallback}
                  alt={promotion.title}
                  className="w-full h-full object-cover"
                />
                <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                  {getStatusBadge(promotion.status)}
                </div>
                {promotion.discount_display && (
                  <div style={{ position: 'absolute', bottom: '12px', right: '12px', background: TERRA, color: '#FCFBF5', padding: '5px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: 700, fontFamily: 'Archivo, sans-serif' }}>
                    {promotion.discount_display}
                  </div>
                )}
              </div>

              {/* Contenu */}
              <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div className="flex items-start gap-2.5">
                  <span style={{ width: '16px', height: '16px', borderRadius: '5px', border: '1px solid rgba(5,16,4,0.14)', flexShrink: 0, marginTop: '4px' }} title={`Couleur: ${promotion.background_color || '#FFFFFF'}`} />
                  <h3 className="line-clamp-2" style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '18px', lineHeight: 1.25, flex: 1 }}>{promotion.title}</h3>
                  <span className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                    {promotion.requires_payment && promotion.stripe_payment_status !== 'paid' && (promotion.promotion_fee > 0 || promotion.service_fee_display) && (
                      <button type="button" onClick={() => navigate('/promotion-checkout/' + promotion.id, { state: { amount: Number(promotion.promotion_fee) || 0 } })} style={{ ...iconBtn, color: TERRA }} title="Payer la promotion">
                        <CreditCard size={15} />
                      </button>
                    )}
                    <button type="button" onClick={() => setEditingPromotion(promotion)} style={iconBtn} title="Modifier">
                      <Edit size={15} />
                    </button>
                    <button type="button" onClick={() => handleDeletePromotion(promotion.id)} style={iconBtn} title="Supprimer">
                      <Trash2 size={15} />
                    </button>
                    <button type="button" onClick={() => handleToggleStatus(promotion.id, promotion.status)} style={iconBtn} title={promotion.status === 'active' ? 'Mettre en pause' : 'Activer'}>
                      {promotion.status === 'active' ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <button type="button" onClick={() => handleViewPerformance(promotion.id)} style={iconBtn} title="Voir les performances">
                      <BarChart3 size={15} />
                    </button>
                  </span>
                </div>

                <p className="line-clamp-2" style={{ margin: '4px 0 8px', fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.68)' }}>
                  {promotion.short_description || promotion.description}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13.5px', marginBottom: '10px' }}>
                  <span className="flex justify-between"><span style={{ color: 'rgba(5,16,4,0.6)' }}>Type</span><strong>{getTypeLabel(promotion.promotion_type)}</strong></span>
                  <span className="flex items-center justify-between">
                    <span style={{ color: 'rgba(5,16,4,0.6)' }}>Prix</span>
                    <span className="flex items-center gap-2">
                      <strong style={{ color: PRIMARY }}>{promotion.final_price_display}</strong>
                      {promotion.original_price > (promotion.discounted_price || promotion.total_price) && (
                        <span style={{ fontSize: '12.5px', color: 'rgba(5,16,4,0.45)', textDecoration: 'line-through' }}>{promotion.original_price.toFixed(2)}€</span>
                      )}
                    </span>
                  </span>
                  {promotion.service_fee_display !== "Gratuit" && (
                    <span className="flex justify-between"><span style={{ color: 'rgba(5,16,4,0.6)' }}>Frais</span><strong style={{ color: TERRA }}>{promotion.service_fee_display}</strong></span>
                  )}
                  <span className="flex justify-between">
                    <span style={{ color: 'rgba(5,16,4,0.6)' }}>Utilisations</span>
                    <strong>{promotion.current_uses}{promotion.max_uses && ` / ${promotion.max_uses}`}</strong>
                  </span>
                </div>

                <div className="flex items-center justify-between" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.55)', paddingTop: '10px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>
                  <span className="flex items-center gap-1.5">
                    <Calendar size={12} />
                    {new Date(promotion.start_date).toLocaleDateString()}
                    {promotion.end_date && ` – ${new Date(promotion.end_date).toLocaleDateString()}`}
                  </span>
                  {promotion.remaining_uses > 0 && <span>{promotion.remaining_uses} restants</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modales */}
      {showCreateModal && (
        <CreatePromotionModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePromotion}
        />
      )}

      {editingPromotion && (
        <EditPromotionModal
          promotion={editingPromotion}
          onClose={() => setEditingPromotion(null)}
          onSubmit={(data) => handleUpdatePromotion(editingPromotion.id, data)}
        />
      )}

      {showPaymentModal && promotionToPay && (
        <PromotionPaymentModal
          promotion={promotionToPay}
          amount={paymentAmount}
          onClose={() => {
            setShowPaymentModal(false);
            setPromotionToPay(null);
            setPaymentAmount(0);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Modal performance individuelle */}
      {selectedPromotionForPerformance && (
        <PromotionPerformanceModal
          promotion={promotions.find(p => p.id === selectedPromotionForPerformance)}
          stats={performanceStats}
          loading={loadingPerformance}
          onClose={() => {
            setSelectedPromotionForPerformance(null);
            setPerformanceStats(null);
          }}
        />
      )}
    </div>
  );
};

export default RestaurantPromotions;

// Modal de performance d'une promotion individuelle
const PromotionPerformanceModal = ({ promotion, stats, loading, onClose }) => {
  const title = promotion?.title || 'Promotion';

  const MetricCard = ({ icon: Icon, label, value, sub, color }) => (
    <div style={{ background: '#FBF9D9', borderRadius: '18px', padding: '16px' }}>
      <span className="flex items-center gap-1.5" style={{ marginBottom: '4px', ...FIELD_LABEL }}>
        <Icon size={13} />{label}
      </span>
      <p style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '22px', color: color || '#051004' }}>{value}</p>
      {sub && <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>{sub}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', width: '100%', maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3" style={{ padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '21px', letterSpacing: '-0.02em' }}>Performances de la pub</h2>
            <p className="truncate" style={{ margin: '2px 0 0', fontSize: '13px', color: 'rgba(5,16,4,0.55)', maxWidth: '340px' }}>{title}</p>
          </div>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Fermer"><X size={17} /></button>
        </div>

        <div style={{ padding: '24px' }}>
          {loading ? (
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="animate-pulse" style={{ background: '#FBF9D9', borderRadius: '18px', height: '96px' }} />
              ))}
            </div>
          ) : !stats ? (
            <div style={{ textAlign: 'center', padding: '40px 16px' }}>
              <BarChart3 size={40} style={{ color: 'rgba(5,16,4,0.2)', margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.6)' }}>Aucune donnée disponible pour cette promotion.</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(5,16,4,0.45)' }}>Les données apparaîtront dès que la pub sera affichée.</p>
            </div>
          ) : (
            <>
              {/* Métriques principales */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" style={{ marginBottom: '22px' }}>
                <MetricCard icon={Eye} label="Impressions" value={stats.impressions?.toLocaleString() ?? '0'} sub="vues totales" />
                <MetricCard icon={MousePointer} label="Clics" value={stats.clicks?.toLocaleString() ?? '0'} sub="clics totaux" />
                <MetricCard icon={Target} label="Conversions" value={stats.conversions?.toLocaleString() ?? '0'} sub="commandes générées" color={PRIMARY} />
                <MetricCard
                  icon={TrendingUp}
                  label="CTR"
                  value={`${(stats.ctr ?? stats.click_through_rate ?? 0).toFixed(2)}%`}
                  sub="taux de clic"
                  color={(stats.ctr ?? 0) >= 2 ? PRIMARY : undefined}
                />
                <MetricCard
                  icon={BarChart3}
                  label="Taux conv."
                  value={`${(stats.conversion_rate ?? 0).toFixed(2)}%`}
                  sub="clics → commandes"
                  color={(stats.conversion_rate ?? 0) >= 5 ? PRIMARY : undefined}
                />
                <MetricCard
                  icon={DollarSign}
                  label="Revenu"
                  value={`${(stats.total_revenue ?? 0).toFixed(2)} €`}
                  sub={`${(stats.rpc ?? stats.revenue_per_click ?? 0).toFixed(2)} € / clic`}
                  color={PRIMARY}
                />
              </div>

              {/* Stats par format d'affichage */}
              {stats.events_by_format && Object.keys(stats.events_by_format).length > 0 && (
                <div style={{ marginBottom: '22px' }}>
                  <h3 style={{ margin: '0 0 10px', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Par format d'affichage</h3>
                  <div style={{ background: '#FBF9D9', borderRadius: '18px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', fontSize: '13.5px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>Format</th>
                          <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>Vues</th>
                          <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>Clics</th>
                          <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>CTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(stats.events_by_format).map(([fmt, data]) => {
                          const ctr = data.impressions > 0 ? (data.clicks / data.impressions * 100) : 0;
                          const labels = { floating_left: 'Flottant gauche', top_banner: 'Bannière haute', section: 'Section promos' };
                          return (
                            <tr key={fmt}>
                              <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)', fontWeight: 600 }}>{labels[fmt] ?? fmt}</td>
                              <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)', textAlign: 'right' }}>{data.impressions.toLocaleString()}</td>
                              <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)', textAlign: 'right' }}>{data.clicks.toLocaleString()}</td>
                              <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)', textAlign: 'right', fontWeight: 600 }}>{ctr.toFixed(1)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Évolution par jour */}
              {stats.events_by_day && stats.events_by_day.length > 0 && (
                <div>
                  <h3 style={{ margin: '0 0 10px', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Évolution sur 30 jours</h3>
                  <div style={{ background: '#FBF9D9', borderRadius: '18px', maxHeight: '220px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '13.5px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>Date</th>
                          <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>Vues</th>
                          <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>Clics</th>
                          <th style={{ textAlign: 'right', padding: '10px 14px', fontSize: '11.5px', fontFamily: 'Archivo, sans-serif', color: 'rgba(5,16,4,0.5)' }}>Conv.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...stats.events_by_day].reverse().map((day) => (
                          <tr key={day.date}>
                            <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)' }}>{new Date(day.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</td>
                            <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)', textAlign: 'right' }}>{day.impressions.toLocaleString()}</td>
                            <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)', textAlign: 'right' }}>{day.clicks.toLocaleString()}</td>
                            <td style={{ padding: '10px 14px', borderTop: '1px solid rgba(5,16,4,0.08)', textAlign: 'right', fontWeight: 600, color: PRIMARY }}>{day.conversions.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant modal pour créer une promotion (simplifié)
const CreatePromotionModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    promotion_type: 'percentage',
    original_price: 0,
    discount_percentage: 0,
    fixed_discount_amount: 0,
    service_price: 0,
    is_paid_service: false,
    hourly_rate: 0,
    is_time_based_pricing: false,
    pricing_unit: 'hour',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    max_uses: '',
    is_featured: false,
    image_url: '',
    video_url: '',
    target_type: 'dish', // 'dish' ou 'menu'
    dish_ids: [],
    menu_ids: [],
    // Nouveaux champs pour le format d'affichage
    display_format: 'section', // floating_left, top_banner, section
    slide_duration: 5, // 5-30 secondes
    media_type: 'image', // image, video
    background_color: '#FFFFFF',
    label: '', // offre_du_jour, nouveau
    cta_type: 'discover', // discover, order
    cta_text: 'Découvrir',
    cta_url: '',
    layout_type: 'dish' // dish, menu
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [showEstimator, setShowEstimator] = useState(true);

  useEffect(() => {
    loadDishesAndMenus();
  }, []);

  const loadDishesAndMenus = async () => {
    try {
      setLoadingData(true);
      const [dishesData, menusData] = await Promise.all([
        getRestaurantDishes(),
        getRestaurantMenus()
      ]);
      setDishes(Array.isArray(dishesData) ? dishesData : []);
      setMenus(Array.isArray(menusData) ? menusData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des plats et menus:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation
    if (!file.type.startsWith('image/')) {
      alert('Le fichier doit être une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB
      alert('L\'image ne doit pas dépasser 5MB');
      return;
    }

    try {
      setUploadingImage(true);

      // Créer FormData pour l'upload
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'promotions');

      // Upload vers Digital Ocean Spaces
      const response = await fetch(`${API_BASE_URL}/management/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('restaurantToken')}`
        },
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const result = await response.json();

      // Mettre à jour le form avec l'URL uploadée
      setFormData(prev => ({ ...prev, image_url: result.image_url }));
      setImagePreview(result.image_url);

    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validation
    if (!file.type.startsWith('video/')) {
      alert('Le fichier doit être une vidéo');
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB
      alert('La vidéo ne doit pas dépasser 50MB');
      return;
    }

    try {
      setUploadingVideo(true);

      // Créer FormData pour l'upload
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'promotions/videos');

      // Upload vers Digital Ocean Spaces
      const response = await fetch(`${API_BASE_URL}/management/upload/video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('restaurantToken')}`
        },
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const result = await response.json();

      // Mettre à jour le form avec l'URL uploadée
      setFormData(prev => ({ ...prev, video_url: result.video_url || result.url }));
      setVideoPreview(result.video_url || result.url);

    } catch (error) {
      console.error('Erreur upload vidéo:', error);
      alert('Erreur lors de l\'upload de la vidéo');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleTargetTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      target_type: type,
      dish_ids: type === 'dish' ? prev.dish_ids : [],
      menu_ids: type === 'menu' ? prev.menu_ids : []
    }));
  };

  const selectDish = (dishId) => {
    setFormData((prev) => {
      const deselect = prev.dish_ids.includes(dishId);
      const nextIds = deselect ? [] : [dishId];
      const dish = dishes.find((d) => d.id === dishId);
      let nextPrice = prev.original_price;
      if (!deselect && dish != null) {
        const p = Number(dish.price);
        nextPrice = Number.isFinite(p) ? Math.round(p * 100) / 100 : 0;
      }
      return { ...prev, dish_ids: nextIds, original_price: nextPrice };
    });
  };

  const selectMenu = (menuId) => {
    setFormData((prev) => {
      const deselect = prev.menu_ids.includes(menuId);
      const nextIds = deselect ? [] : [menuId];
      const menu = menus.find((m) => m.id === menuId);
      let nextPrice = prev.original_price;
      if (!deselect && menu != null) {
        const p = Number(menu.price);
        nextPrice = Number.isFinite(p) ? Math.round(p * 100) / 100 : 0;
      }
      return { ...prev, menu_ids: nextIds, original_price: nextPrice };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (formData.target_type === 'dish' && formData.dish_ids.length === 0) {
      alert('Veuillez sélectionner un plat');
      return;
    }

    if (formData.target_type === 'menu' && formData.menu_ids.length === 0) {
      alert('Veuillez sélectionner un menu');
      return;
    }

    // Validation média
    if (formData.media_type === 'image' && !formData.image_url) {
      alert('Veuillez uploader une image');
      return;
    }

    if (formData.media_type === 'video' && !formData.video_url) {
      alert('Veuillez uploader une vidéo');
      return;
    }

    // Validation format et média
    if (formData.display_format === 'top_banner' && formData.media_type === 'video') {
      alert('La bande en haut ne supporte que les images');
      return;
    }

    // Payload aligné sur le schéma API PromotionCreate (title, description, promotion_type, display_format, media_type, media_url, original_price, discount_percentage, fixed_discount_amount, start_date, end_date, max_uses, is_featured, requires_payment, dish_ids, menu_ids, cta_label, cta_url)
    const mediaUrl = formData.media_type === 'video' ? (formData.video_url || null) : (formData.image_url || null);
    const submitData = {
      title: formData.title.trim(),
      description: (formData.description || '').trim() || null,
      short_description: (formData.short_description || '').trim() || null,
      promotion_type: formData.promotion_type === 'percentage' ? 'discount' : (formData.promotion_type || 'discount'),
      display_format: formData.display_format || null,
      media_type: formData.media_type || null,
      media_url: mediaUrl,
      background_color: formData.background_color || '#FFFFFF',
      label: formData.label || null,
      cta_type: formData.cta_type || 'discover',
      layout_type: formData.layout_type || 'dish',
      original_price: parseMoneyInput(formData.original_price),
      discount_percentage: parseMoneyInput(formData.discount_percentage),
      fixed_discount_amount: parseMoneyInput(formData.fixed_discount_amount),
      start_date: new Date(formData.start_date).toISOString(),
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      max_uses: formData.max_uses !== '' && formData.max_uses != null ? (parseInt(formData.max_uses, 10) || null) : null,
      is_featured: !!formData.is_featured,
      requires_payment: true,
      dish_ids: formData.target_type === 'dish' ? formData.dish_ids : null,
      menu_ids: formData.target_type === 'menu' ? formData.menu_ids : null,
      cta_label: (formData.cta_text || '').trim() || null,
      cta_url: (formData.cta_url || '').trim() || null,
      // Pour le flux paiement côté parent (ignoré par l'API)
      promotion_fee: estimatedPrice,
    };

    onSubmit(submitData);
  };

  const handlePriceCalculated = useCallback((price) => {
    setEstimatedPrice(price);
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3" style={{ padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Nouvelle promotion</h2>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Fermer"><X size={17} /></button>
        </div>
        <div style={{ padding: '24px' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <div>
            <label style={FIELD_LABEL}>Titre</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              style={inputStyle}
              required
            />
          </div>

          <div>
            <label style={FIELD_LABEL}>Description courte</label>
            <input
              type="text"
              value={formData.short_description}
              onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={FIELD_LABEL}>Couleur de fond</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.background_color || '#FFFFFF'}
                onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                style={{ width: '52px', height: '40px', border: '1px solid rgba(5,16,4,0.14)', borderRadius: '12px', cursor: 'pointer', padding: '2px' }}
              />
              <input
                type="text"
                value={formData.background_color || '#FFFFFF'}
                onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                style={{ ...inputStyle, flex: 1 }}
                placeholder="#FFFFFF"
              />
            </div>
          </div>

          {/* Upload d'image */}
          <div>
            <label style={FIELD_LABEL}>Image de promotion</label>
            <div className="flex items-center gap-4">
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" style={{ height: '110px', width: '110px', objectFit: 'cover', borderRadius: '18px' }} />
                  <button
                    type="button"
                    onClick={() => { setImagePreview(null); setFormData(prev => ({ ...prev, image_url: '' })); }}
                    style={{ position: 'absolute', top: '-8px', right: '-8px', border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', borderRadius: '999px', width: '24px', height: '24px', display: 'grid', placeItems: 'center' }}
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div style={{ width: '110px', height: '110px', border: '2px dashed rgba(5,16,4,0.2)', borderRadius: '18px', display: 'grid', placeItems: 'center' }}>
                  <ImageIcon size={26} style={{ color: 'rgba(5,16,4,0.25)' }} />
                </div>
              )}
              <div className="flex-1">
                <label className="flex items-center justify-center gap-2" style={{ ...btnGhost, cursor: 'pointer', display: 'flex' }}>
                  <Upload size={15} />
                  <span>{uploadingImage ? 'Upload en cours...' : 'Choisir une image'}</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                </label>
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>JPG, PNG ou GIF (max. 5MB)</p>
              </div>
            </div>
          </div>

          {/* Type de cible (Plat ou Menu) */}
          <div>
            <label style={FIELD_LABEL}>Promouvoir</label>
            <div className="flex gap-2">
              {[{ v: 'dish', l: 'Un plat' }, { v: 'menu', l: 'Un menu' }].map(({ v, l }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => handleTargetTypeChange(v)}
                  style={{ ...(formData.target_type === v ? { ...btnGhost, background: PRIMARY, color: '#FBF9D9', border: 'none' } : btnGhost), minHeight: '40px' }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Sélection du plat */}
          {formData.target_type === 'dish' && (
            <div>
              <label style={FIELD_LABEL}>Choisir un plat</label>
              {loadingData ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Chargement des plats...</div>
              ) : dishes.length === 0 ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucun plat disponible</div>
              ) : (
                <div style={{ maxHeight: '190px', overflowY: 'auto', background: '#FBF9D9', borderRadius: '18px', padding: '8px' }}>
                  {dishes.map((dish) => (
                    <label key={dish.id} className="flex items-center gap-2.5" style={{ padding: '9px 10px', borderRadius: '12px', cursor: 'pointer' }}>
                      <input type="radio" name="dish_select" checked={formData.dish_ids.includes(dish.id)} onChange={() => selectDish(dish.id)} style={{ accentColor: PRIMARY }} />
                      <span style={{ fontSize: '13.5px' }}>{dish.name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>{formatCurrency(dish.price || 0)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sélection du menu */}
          {formData.target_type === 'menu' && (
            <div>
              <label style={FIELD_LABEL}>Choisir un menu</label>
              {loadingData ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Chargement des menus...</div>
              ) : menus.length === 0 ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucun menu disponible</div>
              ) : (
                <div style={{ maxHeight: '190px', overflowY: 'auto', background: '#FBF9D9', borderRadius: '18px', padding: '8px' }}>
                  {menus.map((menu) => (
                    <label key={menu.id} className="flex items-center gap-2.5" style={{ padding: '9px 10px', borderRadius: '12px', cursor: 'pointer' }}>
                      <input type="radio" name="menu_select" checked={formData.menu_ids.includes(menu.id)} onChange={() => selectMenu(menu.id)} style={{ accentColor: PRIMARY }} />
                      <span style={{ fontSize: '13.5px' }}>{menu.name}</span>
                      {menu.price && <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>{formatCurrency(menu.price || 0)}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Format d'affichage et paramètres visuels */}
          <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '17px' }}>Format d'affichage et paramètres visuels</h3>

            {/* Format d'affichage */}
            <div>
              <label style={FIELD_LABEL}>Format d'affichage</label>
              <div className="grid grid-cols-3 gap-2.5">
                {[
                  { value: 'floating_left', label: 'Flottant à gauche', price: '7$ base' },
                  { value: 'top_banner', label: 'Bande en haut', price: '10$ base' },
                  { value: 'section', label: 'Section promotions', price: '5$ base' }
                ].map((format) => (
                  <button
                    key={format.value}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, display_format: format.value }));
                      if (format.value === 'top_banner') {
                        setFormData(prev => ({ ...prev, media_type: 'image' }));
                      }
                    }}
                    style={optionCard(formData.display_format === format.value)}
                  >
                    <div style={{ fontSize: '13.5px', fontWeight: 600 }}>{format.label}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(5,16,4,0.5)', marginTop: '2px' }}>{format.price}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Type de média */}
            <div>
              <label style={FIELD_LABEL}>Type de média</label>
              <div className="flex gap-2.5">
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, media_type: 'image' }))} className="flex items-center gap-2" style={optionCard(formData.media_type === 'image')}>
                  <ImageIcon size={15} /><span>Image</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, media_type: 'video' }))}
                  disabled={formData.display_format === 'top_banner'}
                  className="flex items-center gap-2"
                  style={{ ...optionCard(formData.media_type === 'video'), opacity: formData.display_format === 'top_banner' ? 0.45 : 1, cursor: formData.display_format === 'top_banner' ? 'not-allowed' : 'pointer' }}
                >
                  <Video size={15} /><span>Vidéo (1-30s)</span>
                </button>
              </div>
            </div>

            {/* Upload vidéo si nécessaire */}
            {formData.media_type === 'video' && (
              <div>
                <label style={FIELD_LABEL}>Vidéo de promotion (1-30s)</label>
                <div className="flex items-center gap-4">
                  {videoPreview ? (
                    <div className="relative">
                      <video src={videoPreview} style={{ height: '110px', width: '110px', objectFit: 'cover', borderRadius: '18px' }} controls />
                      <button
                        type="button"
                        onClick={() => { setVideoPreview(null); setFormData(prev => ({ ...prev, video_url: '' })); }}
                        style={{ position: 'absolute', top: '-8px', right: '-8px', border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', borderRadius: '999px', width: '24px', height: '24px', display: 'grid', placeItems: 'center' }}
                      >
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ width: '110px', height: '110px', border: '2px dashed rgba(5,16,4,0.2)', borderRadius: '18px', display: 'grid', placeItems: 'center' }}>
                      <Video size={26} style={{ color: 'rgba(5,16,4,0.25)' }} />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="flex items-center justify-center gap-2" style={{ ...btnGhost, cursor: 'pointer', display: 'flex' }}>
                      <Upload size={15} />
                      <span>{uploadingVideo ? 'Upload en cours...' : 'Choisir une vidéo'}</span>
                      <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" disabled={uploadingVideo} />
                    </label>
                    <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>MP4, MOV (max. 50MB, 1-30s)</p>
                  </div>
                </div>
              </div>
            )}

            {/* Durée du slide */}
            <div>
              <label style={FIELD_LABEL}>Durée du slide : {formData.slide_duration} secondes</label>
              <input
                type="range"
                min="5"
                max="30"
                value={Number(formData.slide_duration) || 5}
                onChange={(e) => setFormData(prev => ({ ...prev, slide_duration: parseInt(e.target.value, 10) || 5 }))}
                style={{ width: '100%', accentColor: TERRA }}
              />
              <div className="flex justify-between" style={{ fontSize: '11.5px', color: 'rgba(5,16,4,0.5)', marginTop: '2px' }}>
                <span>5s</span>
                <span>30s</span>
              </div>
            </div>

            {/* Label */}
            <div>
              <label style={FIELD_LABEL}>Label</label>
              <select
                value={formData.label || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value || null }))}
                style={inputStyle}
              >
                <option value="">Aucun label</option>
                <option value="offre_du_jour">Offre du jour</option>
                <option value="nouveau">Nouveau</option>
              </select>
            </div>

            {/* Type de layout */}
            <div>
              <label style={FIELD_LABEL}>Type de layout</label>
              <div className="flex gap-2.5 flex-wrap">
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, layout_type: 'dish' }))} style={optionCard(formData.layout_type === 'dish')}>
                  Plat (image/vidéo en haut, texte en bas)
                </button>
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, layout_type: 'menu' }))} style={optionCard(formData.layout_type === 'menu')}>
                  Menu (image à gauche, texte à droite)
                </button>
              </div>
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label style={FIELD_LABEL}>Type de bouton</label>
                <select value={formData.cta_type} onChange={(e) => setFormData(prev => ({ ...prev, cta_type: e.target.value }))} style={inputStyle}>
                  <option value="discover">Découvrir</option>
                  <option value="order">Commander</option>
                </select>
              </div>
              <div>
                <label style={FIELD_LABEL}>Texte du bouton</label>
                <input type="text" value={formData.cta_text} onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))} style={inputStyle} placeholder="Découvrir" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={FIELD_LABEL}>Type</label>
              <select value={formData.promotion_type} onChange={(e) => setFormData({ ...formData, promotion_type: e.target.value })} style={inputStyle}>
                <option value="percentage">Réduction %</option>
                <option value="fixed_amount">Réduction fixe</option>
                <option value="buy_one_get_one">Achetez 1, obtenez 1</option>
                <option value="combo">Menu combo</option>
                <option value="flash_sale">Vente flash</option>
                <option value="seasonal">Saisonnière</option>
              </select>
            </div>

            <div>
              <label style={FIELD_LABEL}>Prix original (€)</label>
              <input
                type="number"
                step="0.01"
                value={Number.isNaN(formData.original_price) ? '' : formData.original_price}
                onChange={(e) => setFormData({ ...formData, original_price: e.target.value === '' ? 0 : (parseFloat(e.target.value) || 0) })}
                style={inputStyle}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={FIELD_LABEL}>Réduction (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={Number.isNaN(formData.discount_percentage) ? '' : formData.discount_percentage}
                onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value === '' ? 0 : (parseFloat(e.target.value) || 0) })}
                style={inputStyle}
              />
            </div>
            <div className="flex items-end">
              <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.5, color: 'rgba(5,16,4,0.6)', background: '#FBF9D9', borderRadius: '16px', padding: '10px 14px', width: '100%' }}>
                <strong>Tarif plateforme</strong> : coût pour diffuser l'annonce (hors prix du plat). Estimé ci-dessous
                après les dates — facturé au paiement Stripe.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={FIELD_LABEL}>Date de début</label>
              <input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} style={inputStyle} required />
            </div>
            <div>
              <label style={FIELD_LABEL}>Date de fin (optionnel)</label>
              <input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} style={inputStyle} min={formData.start_date} />
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px' }}>
            <PromotionEstimator
              onPriceCalculated={handlePriceCalculated}
              displayFormat={formData.display_format}
              slideDuration={formData.slide_duration}
              mediaType={formData.media_type}
              startDate={formData.start_date}
              endDate={formData.end_date || null}
              isFeatured={formData.is_featured}
            />
          </div>

          <div>
            <label style={FIELD_LABEL}>Nombre maximum d'utilisations (optionnel)</label>
            <input
              type="number"
              min="1"
              value={formData.max_uses === undefined || formData.max_uses === null || formData.max_uses === '' ? '' : Number(formData.max_uses)}
              onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
              style={inputStyle}
              placeholder="Illimité si vide"
            />
          </div>

          {/* Résumé du prix */}
          {estimatedPrice > 0 && (
            <div className="flex items-center justify-between" style={{ background: '#DDEECB', borderRadius: '18px', padding: '16px 18px' }}>
              <div className="flex items-center gap-3">
                <DollarSign size={20} style={{ color: PRIMARY }} />
                <div>
                  <div style={{ fontSize: '12.5px', color: 'rgba(5,16,4,0.65)' }}>Tarif plateforme (estimé)</div>
                  <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '22px', color: PRIMARY }}>{formatCurrency(estimatedPrice)}</div>
                </div>
              </div>
              <div style={{ fontSize: '11.5px', color: 'rgba(5,16,4,0.55)' }}>Paiement requis pour activer</div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} style={btnGhost}>Annuler</button>
            <button type="submit" className="inline-flex items-center gap-2" style={btnPrimary}>
              <span>Créer la promotion</span>
              {estimatedPrice > 0 && <span style={{ fontSize: '13px' }}>({formatCurrency(estimatedPrice)})</span>}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

// Composant modal pour éditer une promotion (simplifié)
const EditPromotionModal = ({ promotion, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: promotion.title || '',
    description: promotion.description || '',
    short_description: promotion.short_description || '',
    promotion_type: promotion.promotion_type || 'percentage',
    original_price: promotion.original_price || 0,
    discount_percentage: promotion.discount_percentage || 0,
    service_price: promotion.service_price || 0,
    is_paid_service: promotion.is_paid_service || false,
    start_date: promotion.start_date ? new Date(promotion.start_date).toISOString().split('T')[0] : '',
    end_date: promotion.end_date ? new Date(promotion.end_date).toISOString().split('T')[0] : '',
    max_uses: promotion.max_uses || '',
    is_featured: promotion.is_featured || false,
    image_url: promotion.image_url || '',
    target_type: promotion.dishes && promotion.dishes.length > 0 ? 'dish' : (promotion.menus && promotion.menus.length > 0 ? 'menu' : 'dish'),
    dish_ids: promotion.dishes?.length ? [promotion.dishes[0].dish_id ?? promotion.dishes[0].id] : [],
    menu_ids: promotion.menus?.length ? [promotion.menus[0].menu_id ?? promotion.menus[0].id] : [],
    fixed_discount_amount: promotion.fixed_discount_amount ?? 0,
    display_format: promotion.display_format || 'section',
    media_type: promotion.media_type || 'image',
    cta_label: promotion.cta_label || '',
    cta_url: promotion.cta_url || '',
    background_color: promotion.background_color || '#FFFFFF'
  });

  const [imagePreview, setImagePreview] = useState(promotion.image_url || null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadDishesAndMenus();
    const url = promotion.media_url || promotion.image_url;
    if (url) {
      setImagePreview(url);
    }
  }, []);

  const loadDishesAndMenus = async () => {
    try {
      setLoadingData(true);
      const [dishesData, menusData] = await Promise.all([
        getRestaurantDishes(),
        getRestaurantMenus()
      ]);
      setDishes(Array.isArray(dishesData) ? dishesData : []);
      setMenus(Array.isArray(menusData) ? menusData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des plats et menus:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Le fichier doit être une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image ne doit pas dépasser 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'promotions');

      const response = await fetch(`${API_BASE_URL}/management/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('restaurantToken')}`
        },
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const result = await response.json();
      setFormData(prev => ({ ...prev, image_url: result.image_url }));
      setImagePreview(result.image_url);
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleTargetTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      target_type: type,
      dish_ids: type === 'dish' ? prev.dish_ids : [],
      menu_ids: type === 'menu' ? prev.menu_ids : []
    }));
  };

  const selectDish = (dishId) => {
    setFormData((prev) => {
      const deselect = prev.dish_ids.includes(dishId);
      const nextIds = deselect ? [] : [dishId];
      const dish = dishes.find((d) => d.id === dishId);
      let nextPrice = prev.original_price;
      if (!deselect && dish != null) {
        const p = Number(dish.price);
        nextPrice = Number.isFinite(p) ? Math.round(p * 100) / 100 : 0;
      }
      return { ...prev, dish_ids: nextIds, original_price: nextPrice };
    });
  };

  const selectMenu = (menuId) => {
    setFormData((prev) => {
      const deselect = prev.menu_ids.includes(menuId);
      const nextIds = deselect ? [] : [menuId];
      const menu = menus.find((m) => m.id === menuId);
      let nextPrice = prev.original_price;
      if (!deselect && menu != null) {
        const p = Number(menu.price);
        nextPrice = Number.isFinite(p) ? Math.round(p * 100) / 100 : 0;
      }
      return { ...prev, menu_ids: nextIds, original_price: nextPrice };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.target_type === 'dish' && formData.dish_ids.length === 0) {
      alert('Veuillez sélectionner un plat');
      return;
    }

    if (formData.target_type === 'menu' && formData.menu_ids.length === 0) {
      alert('Veuillez sélectionner un menu');
      return;
    }

    // Payload aligné sur le schéma API PromotionUpdate
    const submitData = {
      title: formData.title.trim(),
      description: (formData.description || '').trim() || null,
      promotion_type: formData.promotion_type === 'percentage' ? 'discount' : (formData.promotion_type || 'discount'),
      display_format: formData.display_format || null,
      media_type: formData.media_type || null,
      media_url: (formData.image_url || '').trim() || null,
      background_color: formData.background_color || '#FFFFFF',
      original_price: parseMoneyInput(formData.original_price),
      discount_percentage: parseMoneyInput(formData.discount_percentage),
      fixed_discount_amount: parseMoneyInput(formData.fixed_discount_amount),
      start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      max_uses: formData.max_uses !== '' && formData.max_uses != null ? (parseInt(formData.max_uses, 10) || null) : null,
      is_featured: !!formData.is_featured,
      dish_ids: formData.target_type === 'dish' ? formData.dish_ids : null,
      menu_ids: formData.target_type === 'menu' ? formData.menu_ids : null,
      cta_label: (formData.cta_label || '').trim() || null,
      cta_url: (formData.cta_url || '').trim() || null,
    };

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(5,16,4,0.5)' }}>
      <div style={{ background: '#FCFBF5', borderRadius: '28px', width: '100%', maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 50px rgba(5,16,4,0.25)' }}>
        <div className="flex items-center justify-between gap-3" style={{ padding: '22px 26px', borderBottom: '1px solid rgba(5,16,4,0.08)' }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Modifier la promotion</h2>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Fermer"><X size={17} /></button>
        </div>
        <div style={{ padding: '24px' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <div>
            <label style={FIELD_LABEL}>Titre</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} style={inputStyle} required />
          </div>

          <div>
            <label style={FIELD_LABEL}>Description courte</label>
            <input type="text" value={formData.short_description} onChange={(e) => setFormData({ ...formData, short_description: e.target.value })} style={inputStyle} />
          </div>

          {/* Upload d'image */}
          <div>
            <label style={FIELD_LABEL}>Image de promotion</label>
            <div className="flex items-center gap-4">
              {imagePreview ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" style={{ height: '110px', width: '110px', objectFit: 'cover', borderRadius: '18px' }} />
                  <button
                    type="button"
                    onClick={() => { setImagePreview(null); setFormData(prev => ({ ...prev, image_url: '' })); }}
                    style={{ position: 'absolute', top: '-8px', right: '-8px', border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', borderRadius: '999px', width: '24px', height: '24px', display: 'grid', placeItems: 'center' }}
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div style={{ width: '110px', height: '110px', border: '2px dashed rgba(5,16,4,0.2)', borderRadius: '18px', display: 'grid', placeItems: 'center' }}>
                  <ImageIcon size={26} style={{ color: 'rgba(5,16,4,0.25)' }} />
                </div>
              )}
              <div className="flex-1">
                <label className="flex items-center justify-center gap-2" style={{ ...btnGhost, cursor: 'pointer', display: 'flex' }}>
                  <Upload size={15} />
                  <span>{uploadingImage ? 'Upload en cours...' : 'Choisir une image'}</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                </label>
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>JPG, PNG ou GIF (max. 5MB)</p>
              </div>
            </div>
          </div>

          {/* Type de cible (Plat ou Menu) */}
          <div>
            <label style={FIELD_LABEL}>Promouvoir</label>
            <div className="flex gap-2">
              {[{ v: 'dish', l: 'Un plat' }, { v: 'menu', l: 'Un menu' }].map(({ v, l }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => handleTargetTypeChange(v)}
                  style={{ ...(formData.target_type === v ? { ...btnGhost, background: PRIMARY, color: '#FBF9D9', border: 'none' } : btnGhost), minHeight: '40px' }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Sélection du plat */}
          {formData.target_type === 'dish' && (
            <div>
              <label style={FIELD_LABEL}>Choisir un plat</label>
              {loadingData ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Chargement des plats...</div>
              ) : dishes.length === 0 ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucun plat disponible</div>
              ) : (
                <div style={{ maxHeight: '190px', overflowY: 'auto', background: '#FBF9D9', borderRadius: '18px', padding: '8px' }}>
                  {dishes.map((dish) => (
                    <label key={dish.id} className="flex items-center gap-2.5" style={{ padding: '9px 10px', borderRadius: '12px', cursor: 'pointer' }}>
                      <input type="radio" name="dish_select_edit" checked={formData.dish_ids.includes(dish.id)} onChange={() => selectDish(dish.id)} style={{ accentColor: PRIMARY }} />
                      <span style={{ fontSize: '13.5px' }}>{dish.name}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>{formatCurrency(dish.price || 0)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sélection du menu */}
          {formData.target_type === 'menu' && (
            <div>
              <label style={FIELD_LABEL}>Choisir un menu</label>
              {loadingData ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Chargement des menus...</div>
              ) : menus.length === 0 ? (
                <div style={{ fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucun menu disponible</div>
              ) : (
                <div style={{ maxHeight: '190px', overflowY: 'auto', background: '#FBF9D9', borderRadius: '18px', padding: '8px' }}>
                  {menus.map((menu) => (
                    <label key={menu.id} className="flex items-center gap-2.5" style={{ padding: '9px 10px', borderRadius: '12px', cursor: 'pointer' }}>
                      <input type="radio" name="menu_select_edit" checked={formData.menu_ids.includes(menu.id)} onChange={() => selectMenu(menu.id)} style={{ accentColor: PRIMARY }} />
                      <span style={{ fontSize: '13.5px' }}>{menu.name}</span>
                      {menu.price && <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>{formatCurrency(menu.price || 0)}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={FIELD_LABEL}>Type</label>
              <select value={formData.promotion_type} onChange={(e) => setFormData({ ...formData, promotion_type: e.target.value })} style={inputStyle}>
                <option value="percentage">Réduction %</option>
                <option value="fixed_amount">Réduction fixe</option>
                <option value="buy_one_get_one">Achetez 1, obtenez 1</option>
                <option value="combo">Menu combo</option>
                <option value="flash_sale">Vente flash</option>
                <option value="seasonal">Saisonnière</option>
              </select>
            </div>

            <div>
              <label style={FIELD_LABEL}>Prix original (€)</label>
              <input
                type="number"
                step="0.01"
                value={Number.isNaN(formData.original_price) ? '' : formData.original_price}
                onChange={(e) => setFormData({ ...formData, original_price: e.target.value === '' ? 0 : (parseFloat(e.target.value) || 0) })}
                style={inputStyle}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label style={FIELD_LABEL}>Réduction (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={Number.isNaN(formData.discount_percentage) ? '' : formData.discount_percentage}
                onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value === '' ? 0 : (parseFloat(e.target.value) || 0) })}
                style={inputStyle}
              />
            </div>

            <div className="flex items-end">
              <p style={{ margin: 0, fontSize: '12px', lineHeight: 1.5, color: 'rgba(5,16,4,0.6)', background: '#FBF9D9', borderRadius: '16px', padding: '10px 14px', width: '100%' }}>
                <strong>Tarif plateforme</strong> : facturé au paiement selon le format et la période (voir création).
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} style={btnGhost}>Annuler</button>
            <button type="submit" style={btnPrimary}>Modifier</button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};
